import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import TemplateLiveBar from '../health/TemplateLiveBar';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import useTemplatePermissions from '../../hooks/useTemplatePermissions';
import {
  getTemplateDeleteBlockedMessage,
  isTemplateArchived,
  isTemplateDeletable,
} from '../../utils/templateArchiveUtils';
import { USE_WHATSAPP_REPORTING } from '../../constants/reportingConfig';
import { TEMPLATE_BASE_PATH } from '../../constants/templateApi';
import ReportingToolbar from '../analytics/ReportingToolbar';
import TemplateRejectionAlert from '../ui/TemplateRejectionAlert';
import { useTemplateDetailsContext } from '../../context/TemplateDetailsContext';
import { isRejectedTemplate } from '../../utils/getRejectionReason';
import useTemplateSx from '../../hooks/useTemplateSx';

export default function TemplateDetailsHeader() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const { canEdit, canDelete, canSend } = useTemplatePermissions();
  const {
    isOrgMode,
    templateName,
    template,
    filters,
    applyDateRange,
    syncReporting,
    isSyncing,
    eventsPending,
    onEdit,
    onSend,
    onDeleteRequest,
  } = useTemplateDetailsContext();

  const isApproved = String(template?.status ?? '').toLowerCase() === 'approved';
  const isArchived = isTemplateArchived(template);
  const isRejected = isRejectedTemplate(template);
  const canDeleteTemplate = canDelete && isArchived && isTemplateDeletable(template);
  const deleteBlockedMessage = getTemplateDeleteBlockedMessage(template);

  const title = isOrgMode ? 'Performance Reports' : templateName;

  const [showHealthBar, setShowHealthBar] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCheckHealth = () => {
    setShowHealthBar(true);
    setRefreshKey((k) => k + 1);
  };

  return (
    <Box sx={{ mb: 2 }}>
      {!isOrgMode ? <TemplateRejectionAlert template={template} /> : null}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>

        <ReportingToolbar
          startDate={filters.start_date}
          endDate={filters.end_date}
          onApplyDateRange={applyDateRange}
          onSync={syncReporting}
          isSyncing={isSyncing}
          eventsPending={eventsPending}
          showSync={USE_WHATSAPP_REPORTING}
        >
          {!isOrgMode ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<MonitorHeartIcon sx={{ color: '#059669', fontSize: 16 }} />}
              onClick={handleCheckHealth}
              sx={{
                borderColor: '#10b981',
                color: '#059669',
                '&:hover': {
                  borderColor: '#059669',
                  bgcolor: '#ecfdf5',
                },
              }}
            >
              Check Health
            </Button>
          ) : null}
          {!isOrgMode && canSend && isApproved && !isArchived ? (
            <Button variant="contained" size="small" onClick={onSend} sx={styles.createButton}>
              Send
            </Button>
          ) : null}
          {!isOrgMode && canEdit && !isArchived && isRejected ? (
            <Button variant="outlined" size="small" onClick={onEdit}>
              Edit
            </Button>
          ) : null}
          {!isOrgMode && canDeleteTemplate ? (
            <Button variant="outlined" color="error" size="small" onClick={onDeleteRequest}>
              Delete
            </Button>
          ) : null}
          {!isOrgMode && canDelete && isArchived && !isTemplateDeletable(template) ? (
            <Tooltip title={deleteBlockedMessage}>
              <span>
                <Button variant="outlined" color="error" size="small" disabled>
                  Delete
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </ReportingToolbar>
      </Box>

      {showHealthBar && !isOrgMode && (
        <TemplateLiveBar
          key={refreshKey}
          template={template}
          templateName={templateName}
          onClose={() => setShowHealthBar(false)}
        />
      )}
    </Box>
  );
}
